import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { 
  Smartphone, Download, Plus, Share, MoreVertical, 
  CheckCircle, ArrowRight, Apple, Chrome
} from 'lucide-react';

export default function AppInstallPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));
    
    // Check if already installed (standalone mode)
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  if (isStandalone || isInstalled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">App Installed!</CardTitle>
            <CardDescription>
              ServicePro is installed on your device. You can access it from your home screen.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => window.location.href = '/'}
            >
              Open App <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 p-4">
      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Smartphone className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Install ServicePro</h1>
          <p className="text-gray-600">Get quick access to manage your jobs, quotes, and customers</p>
        </div>

        {/* Benefits */}
        <Card className="mb-6 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-600" />
              Why Install?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Badge className="bg-teal-100 text-teal-700 mt-0.5">1</Badge>
                <span className="text-gray-700">Quick access from your home screen - no browser needed</span>
              </li>
              <li className="flex items-start gap-3">
                <Badge className="bg-teal-100 text-teal-700 mt-0.5">2</Badge>
                <span className="text-gray-700">Full-screen experience without browser UI</span>
              </li>
              <li className="flex items-start gap-3">
                <Badge className="bg-teal-100 text-teal-700 mt-0.5">3</Badge>
                <span className="text-gray-700">Faster loading and smoother performance</span>
              </li>
              <li className="flex items-start gap-3">
                <Badge className="bg-teal-100 text-teal-700 mt-0.5">4</Badge>
                <span className="text-gray-700">Works offline for viewing saved data</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Android Install */}
        {(isAndroid || (!isIOS && !isAndroid)) && (
          <Card className="mb-6 shadow-lg border-2 border-green-200 bg-green-50/50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <Chrome className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg">Android / Chrome</CardTitle>
                  <CardDescription>Quick one-tap install</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {deferredPrompt ? (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                  onClick={handleInstallClick}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install App Now
                </Button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    If the install button doesn&apos;t appear, follow these steps:
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                      <Badge className="bg-green-600">1</Badge>
                      <div>
                        <p className="font-medium text-gray-900">Tap the menu button</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          Look for <MoreVertical className="w-4 h-4" /> (three dots) in your browser
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                      <Badge className="bg-green-600">2</Badge>
                      <div>
                        <p className="font-medium text-gray-900">Select &quot;Install app&quot; or &quot;Add to Home screen&quot;</p>
                        <p className="text-sm text-gray-600">This option may also say &quot;Install ServicePro&quot;</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                      <Badge className="bg-green-600">3</Badge>
                      <div>
                        <p className="font-medium text-gray-900">Tap &quot;Install&quot; to confirm</p>
                        <p className="text-sm text-gray-600">The app will be added to your home screen</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* iOS Install */}
        {(isIOS || (!isIOS && !isAndroid)) && (
          <Card className="mb-6 shadow-lg border-2 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center">
                  <Apple className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg">iPhone / iPad</CardTitle>
                  <CardDescription>Add to Home Screen</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                  <Badge className="bg-blue-600">1</Badge>
                  <div>
                    <p className="font-medium text-gray-900">Tap the Share button</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      Look for <Share className="w-4 h-4" /> at the bottom of Safari
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                  <Badge className="bg-blue-600">2</Badge>
                  <div>
                    <p className="font-medium text-gray-900">Scroll and tap &quot;Add to Home Screen&quot;</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      Look for <Plus className="w-4 h-4 border rounded" /> Add to Home Screen
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
                  <Badge className="bg-blue-600">3</Badge>
                  <div>
                    <p className="font-medium text-gray-900">Tap &quot;Add&quot; to confirm</p>
                    <p className="text-sm text-gray-600">The app icon will appear on your home screen</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Make sure you&apos;re using Safari browser. Other browsers on iOS don&apos;t support installing web apps.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm">
          <p>ServicePro - Service Management Made Simple</p>
        </div>
      </div>
    </div>
  );
}
